/**
 * AdminEmailOutbox — preview stubbed Writer Digest emails before we
 * wire Resend. Lists the `email_outbox` collection with subject,
 * recipient, status, and an "Open preview" toggle that renders the
 * HTML body inline. Admin-only.
 *
 * Route: /admin/email-outbox
 */
import { useCallback, useEffect, useState } from "react";
import { Loader2, Mail, ChevronRight, ChevronDown, CheckCircle2, PlayCircle } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function token() {
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token") || "";
}

async function api(path, opts = {}) {
  const r = await fetch(`${BASE}${path}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!r.ok) throw new Error(`HTTP ${r.status}: ${await r.text()}`);
  return r.json();
}

export default function AdminEmailOutbox() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("");
  const [openId, setOpenId] = useState(null);
  const [running, setRunning] = useState(false);
  const [runResult, setRunResult] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const qs = statusFilter ? `?status=${statusFilter}` : "";
      const d = await api(`/admin/email-outbox${qs}`);
      setItems(d.items || []);
    } catch (e) {
      setError(e.message || "Could not load outbox");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => { load(); }, [load]);

  const runDigest = async () => {
    setRunning(true);
    setRunResult(null);
    try {
      const d = await api(`/digest/run`, { method: "POST" });
      setRunResult(d);
      await load();
    } catch (e) {
      setError(e.message || "Digest run failed");
    } finally {
      setRunning(false);
    }
  };

  const markSent = async (oid) => {
    try {
      await api(`/admin/email-outbox/${oid}/mark-sent`, { method: "POST" });
      await load();
    } catch (e) {
      setError(e.message || "Could not mark sent");
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6" data-testid="admin-email-outbox">
      <header className="flex items-center justify-between mb-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold">
            Publisher · Admin
          </div>
          <h1 className="text-3xl font-bold mt-1" style={{ fontFamily: "'Lora', serif" }}>
            Email Outbox
          </h1>
          <p className="text-sm text-slate-600 mt-1">
            Stubbed digest emails staged in <code>email_outbox</code>. Real delivery via Resend is wired later — for now,
            operator previews and dry-runs live here.
          </p>
        </div>
        <button
          type="button"
          onClick={runDigest}
          disabled={running}
          className="inline-flex items-center gap-2 rounded-full bg-teal-700 hover:bg-teal-800 text-white px-4 py-2 text-sm font-semibold disabled:opacity-50"
          data-testid="admin-email-outbox-run-digest"
        >
          {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <PlayCircle className="w-4 h-4" />}
          {running ? "Building…" : "Run digest now"}
        </button>
      </header>

      {runResult && (
        <div className="mb-4 rounded-lg border border-teal-200 bg-teal-50 px-4 py-2 text-sm text-teal-800" data-testid="admin-email-outbox-run-result">
          Processed <strong>{runResult.processed}</strong> authors ·
          staged <strong>{runResult.staged}</strong> ·
          skipped <strong>{runResult.skipped}</strong>.
        </div>
      )}

      <div className="mb-4 flex items-center gap-2">
        {["", "staged", "sent", "failed"].map((s) => (
          <button
            key={s || "all"}
            type="button"
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1 rounded-full text-xs font-semibold border ${
              statusFilter === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-700 border-slate-300"
            }`}
            data-testid={`admin-email-outbox-filter-${s || "all"}`}
          >
            {s || "All"}
          </button>
        ))}
      </div>

      {error && (
        <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-sm text-red-800" data-testid="admin-email-outbox-error">
          {error}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500"><Loader2 className="w-4 h-4 animate-spin" /> Loading outbox…</div>
      ) : items.length === 0 ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-500" data-testid="admin-email-outbox-empty">
          <Mail className="w-8 h-8 text-slate-300 mx-auto mb-2" />
          No emails staged yet. Click <strong>Run digest now</strong> above to build this week's digests.
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((it) => (
            <div key={it.id} className="rounded-xl border border-slate-200 bg-white overflow-hidden" data-testid={`admin-email-outbox-row-${it.id}`}>
              <button
                type="button"
                onClick={() => setOpenId(openId === it.id ? null : it.id)}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-slate-50 text-left"
              >
                {openId === it.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                <Mail className="w-4 h-4 text-slate-400" />
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold text-slate-900 truncate">{it.subject}</div>
                  <div className="text-xs text-slate-500">
                    to <strong>{it.to_email}</strong> · staged {new Date(it.created_at).toLocaleString()}
                  </div>
                </div>
                <span className={`text-[10px] uppercase tracking-widest font-bold px-2 py-0.5 rounded-full ${
                  it.status === "staged" ? "bg-amber-100 text-amber-800" :
                  it.status === "sent"   ? "bg-emerald-100 text-emerald-800" :
                                            "bg-red-100 text-red-800"
                }`}>{it.status}</span>
              </button>
              {openId === it.id && (
                <div className="border-t border-slate-100 bg-slate-50 p-4" data-testid={`admin-email-outbox-preview-${it.id}`}>
                  <div className="flex items-center justify-end gap-2 mb-3">
                    {it.status === "staged" && (
                      <button
                        type="button"
                        onClick={() => markSent(it.id)}
                        className="inline-flex items-center gap-1 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white px-3 py-1 text-xs font-semibold"
                        data-testid={`admin-email-outbox-mark-sent-${it.id}`}
                      >
                        <CheckCircle2 className="w-3 h-3" />
                        Mark as sent (dry-run)
                      </button>
                    )}
                  </div>
                  <iframe
                    title={`Email preview ${it.id}`}
                    srcDoc={it.html_body}
                    className="w-full h-[560px] rounded-lg border border-slate-200 bg-white"
                    sandbox=""
                    data-testid={`admin-email-outbox-iframe-${it.id}`}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
